/**
 * Preset dropdown — pick a named layout (race/tournament) and apply it.
 * Applying wipes filled-in hint data and resets counts/always/sometimes kinds.
 */
import { useState, useRef, useEffect } from 'react';
import { ChevronDown, BookTemplate, AlertTriangle } from 'lucide-react';
import { useTracker } from '@/contexts/tracker-context';
import { LAYOUT_PRESETS, type LayoutPreset } from './data/layout-presets';

export function PresetDropdown() {
  const { dispatch } = useTracker();
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState<LayoutPreset | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const apply = (preset: LayoutPreset) => {
    dispatch({
      type: 'APPLY_LAYOUT',
      layout: {
        nbGoals: preset.nbGoals,
        nbSometimes: preset.nbSometimes,
        nbBarren: preset.nbBarren,
        isWoth: preset.isWoth,
        alwaysHintNames: preset.alwaysHintNames,
        sometimesKinds: preset.sometimesKinds,
        startingItems: preset.startingItems,
      },
    });
    setConfirming(null);
    setOpen(false);
  };

  return (
    <>
      <div ref={containerRef} className="relative">
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white transition-colors text-sm"
          title="Apply a preset layout"
        >
          <BookTemplate size={16} />
          Preset
          <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <div className="absolute z-50 top-full right-0 mt-1 w-[320px] bg-gray-800 border border-gray-600 rounded-lg shadow-xl overflow-hidden">
            {LAYOUT_PRESETS.map((preset) => (
              <button
                key={preset.id}
                onClick={() => setConfirming(preset)}
                className="w-full text-left px-3 py-2 hover:bg-gray-700 transition-colors border-b border-gray-700/50 last:border-b-0"
              >
                <div className="text-sm font-medium text-white">{preset.label}</div>
                {preset.description && (
                  <div className="text-[11px] text-gray-400 mt-0.5">{preset.description}</div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {confirming && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={() => setConfirming(null)}
        >
          <div
            className="bg-gray-900 border border-gray-700 rounded-lg shadow-2xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-gray-700 flex items-center gap-2">
              <AlertTriangle size={16} className="text-yellow-400" />
              <h2 className="text-sm font-bold text-white">Apply preset?</h2>
            </div>
            <div className="p-4 text-sm text-gray-300 space-y-2">
              <p>
                Apply <span className="text-zelda-gold font-medium">{confirming.label}</span>?
              </p>
              <p className="text-xs text-gray-400">
                This will reset counts, always selection, and sometimes kinds — and clear all
                filled-in hints.
              </p>
            </div>
            <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-700">
              <button
                onClick={() => setConfirming(null)}
                className="px-3 py-1.5 text-xs text-gray-400 hover:text-white border border-gray-700 rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => apply(confirming)}
                className="px-3 py-1.5 text-xs font-medium bg-zelda-gold/20 text-zelda-gold border border-zelda-gold/30 rounded hover:bg-zelda-gold/30"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
